import {
  ContentCopyRounded as ContentCopyRoundedIcon,
  EmailOutlined as EmailOutlinedIcon,
} from '@mui/icons-material';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useCallback, useMemo } from 'react';
import { useSnackBarContext } from '@/contexts/SnackbarContext';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { ColActions } from '@/pages/ValuationsV2/components/Col';
import { useLeadAnalyst } from '../../hooks/useLeadAnalyst';
import { useValuationsStore } from '../../store/valuationsStore';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1,
    color: 'inherit',
    placeContent: 'start',
  },
  // avatar: (theme: Theme) => ({
  //   color: 'inherit',
  //   backgroundColor: 'color-mix(in srgb, currentColor 15%, transparent)',
  //   width: 'clamp(2rem, 10vw, 4.2rem)',
  //   height: 'auto',
  //   aspectRatio: '1/1',
  //   '& svg': {
  //     fontSize: 'clamp(2rem, 6vw, 4rem)',
  //     color: theme.palette.primary.main,
  //   },
  // }),
  analystName: (theme: Theme) => ({
    fontWeight: 600,
    fontSize: theme.fontSizes.base,
    color: 'inherit',
  }),
  email: (theme: Theme) => ({
    fontSize: theme.fontSizes.smaller,
    color: theme.palette.text.secondary,
  }),
  header: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    margin: 'auto 0 0.5rem',
  },
  helperText: (theme: Theme) => ({
    color: 'inherit',
    fontSize: theme.fontSizes.smaller,
    lineHeight: 1.4,
  }),
  actionButton: {
    width: '100%',
  },
  iconButton: {
    width: '100%',
    minWidth: '3.5rem',
  },
};

type ErrorStateProps = {
  message: string;
};

const ErrorState = ({ message }: ErrorStateProps) => (
  <Typography variant='body2' color='error'>
    {message}
  </Typography>
);

type EmptyStateProps = {
  message: string;
};

const EmptyState = ({ message }: EmptyStateProps) => (
  <Typography variant='body2' sx={styles.helperText}>
    {message}
  </Typography>
);

type LeadAnalystData = {
  name: string;
  email: string;
};

type LeadAnalystContentProps = {
  analyst: LeadAnalystData;
};

const LeadAnalystActions = ({ analyst }: LeadAnalystContentProps) => {
  const mailtoHref = useMemo(() => `mailto:${analyst.email}`, [analyst.email]);
  const snackbarContext = useSnackBarContext();
  const { copy } = useCopyToClipboard();

  const handleCopyEmail = useCallback(async (): Promise<void> => {
    if (!analyst?.email) {
      return;
    }

    const success = await copy(analyst.email);

    if (!snackbarContext?.setSnack) {
      return;
    }

    if (success) {
      snackbarContext.setSnack({
        open: true,
        severity: 'success',
        message: 'Email copied to clipboard.',
      });
    } else {
      snackbarContext.setSnack({
        open: true,
        severity: 'error',
        message: 'Unable to copy email. Please try again.',
      });
    }
  }, [analyst?.email, copy, snackbarContext]);

  return (
    <ColActions
      sx={{
        gridTemplateColumns: 'auto 1fr',
      }}
    >
      <Tooltip title='Copy email address'>
        <IconButton
          aria-label='Copy email address'
          color='primary'
          onClick={handleCopyEmail}
          data-id='lead-analyst-copy-email-button'
          sx={styles.iconButton}
        >
          <ContentCopyRoundedIcon fontSize='small' />
        </IconButton>
      </Tooltip>
      <Tooltip title='Email lead analyst'>
        <Button
          variant='outlined'
          sx={styles.actionButton}
          size='small'
          startIcon={<EmailOutlinedIcon fontSize='small' />}
          href={mailtoHref}
          data-id='lead-analyst-contact-button'
        >
          Email
        </Button>
      </Tooltip>
    </ColActions>
  );
};

const LeadAnalystView = () => {
  const selectedAssetDetails = useValuationsStore.use.selectedAssetDetails();

  const countryIsoCode = useMemo(
    () => selectedAssetDetails?.country?.isoCode ?? null,
    [selectedAssetDetails?.country?.isoCode],
  );

  const { leadAnalyst, loading, error } = useLeadAnalyst(countryIsoCode);

  if (!selectedAssetDetails || loading) {
    return null;
  }

  if (error) {
    return <ErrorState message='We were unable to load the lead analyst details right now.' />;
  }

  if (!leadAnalyst) {
    return <EmptyState message='Lead analyst information is not available for this asset.' />;
  }

  return (
    <>
      <Box sx={styles.container} data-id='lead-analyst-card'>
        <Box component='header' sx={styles.header}>
          {/* <Avatar sx={styles.avatar}>
          <AccountCircleIcon fontSize='large' />
        </Avatar> */}
          <Typography component='span' sx={styles.analystName}>
            {leadAnalyst.name}
          </Typography>
          <Typography component='span' variant='caption' sx={styles.email}>
            {leadAnalyst.email}
          </Typography>
        </Box>
      </Box>
      <LeadAnalystActions analyst={leadAnalyst} />
    </>
  );
};

export { LeadAnalystView };
